import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Building2,
  Globe,
  Link2,
  Plus,
  Search,
  Unlink,
} from "lucide-react";
import {
  addAssistantToPlatform,
  getNativeClientPlatforms,
  removeAssistantFromPlatform,
} from "../../../../services/native-client-platform";
import { getAssistantNativeClientPlatforms } from "../../../../services/assistant";
import UserContext from "../../../../context/users";
import { AdminRole } from "../../../../constants";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ConfirmDialog,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Badge } from "../../../../components/ui/badge";
import { EmptyState } from "../../../../components/ui/empty-state";
import { Spinner } from "../../../../components/ui/spinner";
import { Tooltip } from "../../../../components/ui/tooltip";
import useDebounce from "../../../../hooks/useDebounce";
import { cn } from "../../../../lib/cn";
import { toast } from 'sonner'

export default function ClientsManager({
  open,
  onClose,
  assistant,
  onAssociationChange,
}) {
  const [user] = useContext(UserContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [associated, setAssociated] = useState([]);
  const [associatedLoading, setAssociatedLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmRemoveTarget, setConfirmRemoveTarget] = useState(null);
  const debouncedSearch = useDebounce(searchTerm, 350);

  const isAdmin = user?.role === AdminRole;
  const associatedIds = useMemo(
    () => new Set(associated.map((c) => c.id)),
    [associated]
  );

  const fetchAssociated = useCallback(async () => {
    if (!assistant?.id) return;
    setAssociatedLoading(true);
    try {
      const { data } = await getAssistantNativeClientPlatforms(assistant.id);
      setAssociated(data || []);
      onAssociationChange?.(data || []);
    } catch (e) {
      console.error(e);
      toast.error("Falha ao carregar clientes vinculados");
    } finally {
      setAssociatedLoading(false);
    }
  }, [assistant?.id, onAssociationChange]);

  const fetchClients = useCallback(
    async (term) => {
      if (!open) return;
      setSearchLoading(true);
      try {
        const filters = { perPage: 25 };
        if (!isAdmin && user?.companyID) {
          filters.companyID = user.companyID;
        }
        if (term) {
          filters.search = term;
          filters.searchField = "name";
        }
        const { data } = await getNativeClientPlatforms(filters);
        setSearchResults(data || []);
      } catch (e) {
        console.error(e);
        toast.error("Falha ao carregar clientes");
      } finally {
        setSearchLoading(false);
      }
    },
    [open, isAdmin, user?.companyID]
  );

  useEffect(() => {
    if (open) {
      fetchAssociated();
      fetchClients(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedSearch]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSearchResults([]);
      setConfirmRemoveTarget(null);
    }
  }, [open]);

  const handleAdd = useCallback(
    async (client) => {
      if (associatedIds.has(client.id)) return;
      setActionLoading({ type: "add", id: client.id });
      try {
        await addAssistantToPlatform(Number(client.id), Number(assistant.id));
        toast.success(`${client.name} vinculado ao assistente`);
        await fetchAssociated();
      } catch (e) {
        console.error(e);
        toast.error(
          e?.data?.message
            ? `Falha ao vincular: ${e.data.message}`
            : "Falha ao vincular cliente"
        );
      } finally {
        setActionLoading(null);
      }
    },
    [assistant?.id, associatedIds, fetchAssociated]
  );

  const handleRemove = useCallback(async () => {
    if (!confirmRemoveTarget) return;
    setActionLoading({ type: "remove", id: confirmRemoveTarget.id });
    try {
      await removeAssistantFromPlatform(
        Number(confirmRemoveTarget.id),
        Number(assistant.id)
      );
      toast.success(`${confirmRemoveTarget.name} desvinculado`);
      setConfirmRemoveTarget(null);
      await fetchAssociated();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.data?.message
          ? `Falha ao desvincular: ${e.data.message}`
          : "Falha ao desvincular cliente"
      );
    } finally {
      setActionLoading(null);
    }
  }, [confirmRemoveTarget, assistant?.id, fetchAssociated]);

  const availableResults = useMemo(
    () => searchResults.filter((c) => !associatedIds.has(c.id)),
    [searchResults, associatedIds]
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} size="2xl">
        <DialogHeader
          icon={Link2}
          title="Clientes vinculados"
          description={`Conecte ${assistant?.name || "este assistente"} a uma ou mais plataformas de cliente.`}
        />

        <DialogContent>
          <div className="flex flex-col gap-5">
            <section className="flex flex-col gap-2.5">
              <SectionTitle
                icon={Building2}
                title="Vinculados"
                badge={associated.length}
              />
              {associatedLoading ? (
                <div
                  className="flex items-center justify-center rounded-xl py-8"
                  style={{
                    background: "var(--dash-surface-subtle)",
                    border: "1px solid var(--dash-border)",
                  }}
                >
                  <Spinner size={16} />
                </div>
              ) : associated.length === 0 ? (
                <div
                  className="rounded-xl px-4 py-6 text-center text-xs"
                  style={{
                    background: "var(--dash-surface-subtle)",
                    border: "1px dashed var(--dash-border-strong)",
                    color: "var(--dash-text-muted)",
                  }}
                >
                  Nenhum cliente vinculado ainda. Use a busca abaixo para
                  associar.
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {associated.map((client) => (
                    <ClientRow
                      key={client.id}
                      client={client}
                      action={
                        <Tooltip content="Desvincular">
                          <Button
                            variant="danger"
                            size="icon-sm"
                            onClick={() => setConfirmRemoveTarget(client)}
                            loading={
                              actionLoading?.type === "remove" &&
                              actionLoading?.id === client.id
                            }
                          >
                            <Unlink size={13} />
                          </Button>
                        </Tooltip>
                      }
                    />
                  ))}
                </ul>
              )}
            </section>

            <div
              className="h-px w-full"
              style={{ background: "var(--dash-border)" }}
            />

            <section className="flex flex-col gap-2.5">
              <SectionTitle icon={Plus} title="Adicionar cliente" />

              <Input
                leftIcon={Search}
                placeholder="Buscar plataforma de cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <div
                className="dash-scroll max-h-72 overflow-y-auto rounded-xl"
                style={{
                  background: "var(--dash-surface-subtle)",
                  border: "1px solid var(--dash-border)",
                }}
              >
                {searchLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Spinner size={18} />
                  </div>
                ) : availableResults.length === 0 ? (
                  <EmptyState
                    icon={Building2}
                    title={
                      debouncedSearch
                        ? "Nenhum resultado"
                        : "Sem clientes disponíveis"
                    }
                    description={
                      debouncedSearch
                        ? `Nenhuma plataforma encontrada para "${debouncedSearch}".`
                        : "Todos os clientes elegíveis já estão vinculados ou nenhum existe."
                    }
                  />
                ) : (
                  <ul className="flex flex-col">
                    {availableResults.map((client, i) => (
                      <li
                        key={client.id}
                        className={cn(
                          "px-3 py-2.5",
                          i !== availableResults.length - 1 && "border-b"
                        )}
                        style={{
                          borderColor:
                            i !== availableResults.length - 1
                              ? "var(--dash-border)"
                              : undefined,
                        }}
                      >
                        <ClientRow
                          client={client}
                          variant="search"
                          action={
                            <Button
                              variant="primary"
                              size="sm"
                              leftIcon={Plus}
                              onClick={() => handleAdd(client)}
                              loading={
                                actionLoading?.type === "add" &&
                                actionLoading?.id === client.id
                              }
                            >
                              Vincular
                            </Button>
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
        </DialogContent>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRemoveTarget}
        onClose={() => setConfirmRemoveTarget(null)}
        onConfirm={handleRemove}
        title="Desvincular cliente?"
        description={
          confirmRemoveTarget
            ? `${assistant?.name || "Este assistente"} deixará de estar associado a "${confirmRemoveTarget.name}". A associação pode ser refeita depois.`
            : ""
        }
        confirmLabel="Desvincular"
        loading={
          actionLoading?.type === "remove" &&
          actionLoading?.id === confirmRemoveTarget?.id
        }
      />
    </>
  );
}

function SectionTitle({ icon: Icon, title, badge }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} style={{ color: "var(--dash-primary)" }} />
      <h3
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: "var(--dash-text-soft)" }}
      >
        {title}
      </h3>
      {typeof badge === "number" && badge > 0 && (
        <Badge variant="primary">{badge}</Badge>
      )}
    </div>
  );
}

function ClientRow({ client, action, variant = "associated" }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3",
        variant === "associated" && "rounded-xl p-3"
      )}
      style={
        variant === "associated"
          ? {
              background: "var(--dash-surface-subtle)",
              border: "1px solid var(--dash-border)",
            }
          : undefined
      }
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={{
          background: "var(--dash-primary-soft)",
          border: "1px solid var(--dash-primary-border)",
          color: "var(--dash-primary)",
        }}
      >
        {client.logo ? (
          <img
            src={client.logo}
            alt={client.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <Building2 size={14} strokeWidth={2.2} />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span
            className="truncate text-sm font-semibold"
            style={{ color: "var(--dash-text)" }}
          >
            {client.name}
          </span>
          {client.isActive === false && <Badge variant="muted">Inativo</Badge>}
        </div>
        {(client.subdomain || client.customURL) && (
          <span
            className="flex items-center gap-1 truncate text-[11px]"
            style={{ color: "var(--dash-text-muted)" }}
          >
            <Globe size={10} />
            {client.customURL || `${client.subdomain}.verboo.com.br`}
          </span>
        )}
      </div>

      <div className="shrink-0">{action}</div>
    </div>
  );
}
